import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { TrendingUp, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState('');

  const handleDelete = async () => {
    if (confirmation !== 'SUPPRIMER') {
      toast.error('Tapez exactement SUPPRIMER pour confirmer');
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Compte supprimé avec succès');
      logout();
      navigate('/');
    } catch (err) {
      toast.error('Erreur lors de la suppression. Contactez contact@donotbull.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-2 justify-center mb-8 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-8 h-8 bg-[#ff6b35] rounded-lg flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-black" />
          </div>
          <span className="font-black text-white text-lg">Do Not Bull</span>
        </div>

        <div className="bg-gray-900/50 border border-red-500/20 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">Supprimer mon compte</h1>
              <p className="text-gray-500 text-sm">Cette action est irréversible</p>
            </div>
          </div>

          {step === 1 && (
            <div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-300">
                    <p className="font-semibold mb-2">En supprimant votre compte :</p>
                    <ul className="space-y-1 text-red-300/80">
                      <li>• Toutes vos données seront supprimées</li>
                      <li>• Votre portfolio sera effacé</li>
                      <li>• Votre progression dans l'Académie sera perdue</li>
                      <li>• Votre abonnement sera annulé</li>
                    </ul>
                  </div>
                </div>
              </div>

              {user && (
                <p className="text-gray-400 text-sm mb-6 text-center">
                  Compte : <span className="text-white font-semibold">{user.email}</span>
                </p>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-300"
                  onClick={() => navigate('/dashboard')}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => setStep(2)}
                  data-testid="delete-account-step1"
                >
                  Continuer
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-gray-400 text-sm mb-4">
                Pour confirmer, tapez <span className="text-white font-bold">SUPPRIMER</span> ci-dessous :
              </p>
              <input
                type="text"
                value={confirmation}
                onChange={e => setConfirmation(e.target.value.toUpperCase())}
                placeholder="SUPPRIMER"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-red-500 mb-4 font-mono tracking-widest"
                data-testid="delete-confirmation-input"
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-300"
                  onClick={() => setStep(1)}
                >
                  Retour
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDelete}
                  disabled={loading || confirmation !== 'SUPPRIMER'}
                  data-testid="delete-account-confirm"
                >
                  {loading ? 'Suppression...' : 'Supprimer définitivement'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Des questions ? <a href="mailto:contact@donotbull.com" className="text-[#ff6b35]">contact@donotbull.com</a>
        </p>
      </div>
    </div>
  );
}
